import React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { cn } from '../../../../lib/utils';

/**
 * "Skip identity columns" row for the grid copy / export menus.
 *
 * Controls the persisted `insertSkipIdentity` app setting used by generated
 * INSERTs (Copy INSERT / Export SQL). Off by default: identity values are kept
 * and MSSQL output is wrapped in `SET IDENTITY_INSERT ON/OFF` — we never
 * silently change IDs. Turning it on drops identity columns so the target
 * assigns fresh ones.
 *
 * A menu checkbox item (UI/UX batch C2 R13): arrow keys reach it with the rest of
 * the menu, Space / Enter toggles it, and the menu stays open so the copy or
 * export can follow. It keeps a real checkbox box in both states (the earlier
 * popover row's look), drawn beside the label. Self-contained (reads the store,
 * writes the store, fires analytics) so both results grids can drop it into their
 * menus unchanged.
 */
export const SkipIdentityToggleRow = React.memo(function SkipIdentityToggleRow({
  testId = 'skip-identity-toggle',
}: {
  /** Distinguishes the copy menu's row from the export menu's row. */
  testId?: string;
}) {
  const skipIdentity = useAppStore((state) => state.insertSkipIdentity);
  const setInsertSkipIdentity = useAppStore((state) => state.setInsertSkipIdentity);
  const { track } = useAnalytics();

  return (
    <DropdownMenuPrimitive.CheckboxItem
      checked={skipIdentity}
      onCheckedChange={(checked) => {
        const next = checked === true;
        track('feature:results_skip_identity', { enabled: next });
        setInsertSkipIdentity(next);
      }}
      // Keep the menu open: the toggle configures the INSERT the user is about to copy / export.
      onSelect={(event) => event.preventDefault()}
      className="relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground"
      title="Applies to Copy INSERT and Export SQL (INSERT). Off keeps identity values, wrapping MSSQL scripts in SET IDENTITY_INSERT ON/OFF."
      data-testid={testId}
    >
      <span
        aria-hidden
        className={cn('flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-primary shadow', skipIdentity && 'bg-primary text-primary-foreground')}
        data-testid={`${testId}-box`}
      >
        {skipIdentity && <Check className="h-3 w-3" />}
      </span>
      Skip identity columns
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
